import { Alert, Button, Mono, Skeleton, cx } from "./ui";
import { useTranslation } from "react-i18next";
import { formatBytes, relativeTime } from "../lib/format";
import type { ContainerHealth, ContainerState, RuntimeReport } from "../lib/types";

/**
 * What the container is doing right now.
 *
 * This exists because "why does my app keep dying" is the question people
 * actually have, and Docker already knows the answer: a restart count, an exit
 * code, and a health check result. None of that was reaching anyone before.
 *
 * CPU and memory come from the monitoring service, which samples `docker stats`
 * on an interval — so readings exist without anyone watching. They only appear
 * on servers with monitoring configured; when that is not the case, or nothing
 * has been recorded yet, the panel says so rather than drawing gauges that
 * would read as idle.
 */

const STATE_STYLES: Record<ContainerState, string> = {
  running: "bg-mint/15 text-mint",
  restarting: "bg-warn/15 text-warn",
  created: "bg-warn/15 text-warn",
  paused: "bg-raised text-fg-muted",
  exited: "bg-raised text-fg-muted",
  dead: "bg-danger/15 text-danger",
  unknown: "bg-raised text-fg-muted",
};

/**
 * Which sentence a health state gets, or none.
 *
 * Keys rather than words: this is module scope and has no translator, and the
 * one caller is a component that does. `none` stays null — a container with no
 * healthcheck defined is not unhealthy, and saying anything at all here would
 * imply otherwise.
 */
const HEALTH_KEYS: Record<ContainerHealth, string | null> = {
  healthy: "runtime.healthy",
  unhealthy: "runtime.unhealthy",
  starting: "runtime.starting",
  none: null,
};

/*
 * Reports only. Restarting is an action on the card that frames this panel,
 * beside every other card action on the page — it used to be a bare link
 * floated inside the status row, which put one control in a different place
 * from all the others.
 */
/**
 * `cpuPercent` is docker's: 100 is one core, whatever the plan allows. Shown
 * raw it says 42% of a two-core app that is using a fifth of what it bought,
 * sits beside a memory bar that *is* a share of the limit, overflows its own
 * track at 101%, and -- since the scaling card moved onto this page --
 * contradicts the figure the autoscaler acts on. Divided by the allowance it
 * is the same question as memory, on the same scale, in both places.
 */
const shareOfAllowance = (cpuPercent: number | null, cpuLimit: number | undefined) =>
  cpuPercent === null || !cpuLimit || !(cpuLimit > 0) ? cpuPercent : cpuPercent / cpuLimit;

export function RuntimePanel({
  report,
  loading,
  error,
  onRetry,
  cpuLimit,
}: {
  report: RuntimeReport | undefined;
  loading: boolean;
  error?: Error | null;
  onRetry?: () => void;
  /** Cores the plan allows. Without it the CPU reading stays docker's own. */
  cpuLimit?: number;
}) {
  const { t } = useTranslation("database");
  if (loading) return <Skeleton className="h-28 w-full" />;

  // An unavailable runtime response is useful state from the API. A failed
  // request is different: do not turn a control-plane failure into "stopped".
  if (error) {
    return (
      <div className="space-y-3">
        <Alert>
          {t("runtime.reportFailed")}
        </Alert>
        {onRetry && (
          <Button variant="secondary" size="sm" onClick={onRetry}>
            {t("runtime.retry")}
                  </Button>
        )}
      </div>
    );
  }

  if (report?.unavailable) {
    return (
      <Alert tone="warning">
        {t("runtime.unreachable")}
      </Alert>
    );
  }

  const container = report?.container;

  if (!container) {
    return (
      <p className="text-sm text-fg-muted">
        {t("runtime.noContainer")}
      </p>
    );
  }

  const healthKey = HEALTH_KEYS[container.health];
  const healthLabel = healthKey ? t(healthKey as "runtime.healthy") : null;
  const crashLooping = container.restartCount >= 3 && container.state !== "running";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <span
          className={cx(
            "rounded px-2 py-0.5 text-xs font-medium",
            STATE_STYLES[container.state],
          )}
        >
          {container.state}
        </span>

        {healthLabel && (
          <span
            className={cx(
              "text-sm",
              container.health === "unhealthy"
                ? "font-medium text-danger"
                : "text-fg-muted",
            )}
          >
            {healthLabel}
          </span>
        )}

        {/* `!=` on purpose: a runtime report missing the field entirely must
            not render "up NaNd". */}
        {container.uptimeSeconds != null && (
          <span className="text-sm text-fg-subtle">
            up {formatUptime(container.uptimeSeconds)}
          </span>
        )}

        {/* An app can run several containers. Everything else on this panel
            describes the first of them, so say so rather than letting a
            reader take one container's uptime for the whole app's. */}
        {(report?.containers ?? 1) > 1 && (
          <span className="text-sm text-fg-subtle">{t("runtime.ofReplicas", { count: report!.containers })}</span>
        )}
      </div>

      {/*
        Surfaced above the detail grid rather than inside it: a container that
        has restarted repeatedly is the most important fact on this page, and
        the exit explanation is what someone needs to act on.
      */}
      {report?.explanation && (
        <Alert tone={container.state === "running" ? "info" : "warning"}>
          {report.explanation}
        </Alert>
      )}

      {crashLooping && (
        <Alert>
          <span className="font-medium">
            This container has restarted {container.restartCount} times.
          </span>{" "}
          {t("runtime.checkLog")}
        </Alert>
      )}

      <dl className="grid gap-4 text-sm sm:grid-cols-3">
        <Detail label={t("runtime.restarts")}>
          <span
            className={cx(
              "tabular-nums",
              container.restartCount > 0 && "text-warn",
            )}
          >
            {container.restartCount}
          </span>
        </Detail>
        <Detail label={t("runtime.started")}>
          {container.startedAt ? relativeTime(container.startedAt) : "—"}
        </Detail>
        <Detail label={t("runtime.container")}>
          {container.id ? <Mono className="text-xs">{container.id}</Mono> : "—"}
        </Detail>
        {container.image && (
          <div className="sm:col-span-3">
            <Detail label={t("runtime.image")}>
              <Mono className="text-xs break-all">{container.image}</Mono>
            </Detail>
          </div>
        )}
      </dl>

      {/*
        Charts and gauges only when there is a real number to draw — a flat
        line would read as "idle". The monitoring service samples on an
        interval, so the gap here is either a server with monitoring
        unconfigured, or a collector that has not recorded anything for this
        app yet.
      */}
      {report?.metricsAvailable && report.metrics ? (
        <div className="space-y-4 border-t border-line pt-3">
          <div className="grid gap-4 sm:grid-cols-2">
            <UsageChart
              label="CPU"
              value={shareOfAllowance(report.metrics.cpuPercent, cpuLimit)}
              points={report.history.map((s) => shareOfAllowance(s.cpuPercent, cpuLimit))}
            />
            <UsageChart
              label={t("runtime.memory")}
              value={ratio(report.metrics.memoryUsedBytes, report.metrics.memoryTotalBytes)}
              detail={pair(report.metrics.memoryUsedBytes, report.metrics.memoryTotalBytes)}
              points={report.history.map((s) => ratio(s.memoryUsedBytes, s.memoryTotalBytes))}
            />
          </div>

          {/*
            Network and disk came back in the same sample as CPU and memory and
            were being dropped on the floor. They have no ceiling to draw a bar
            against — a container has no "total bytes transferred" — so they are
            counters, not gauges.
          */}
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
            <Counter label={t("runtime.netIn")} mb={report.metrics.networkInputMb} />
            <Counter label={t("runtime.netOut")} mb={report.metrics.networkOutputMb} />
            <Counter label={t("runtime.diskRead")} mb={report.metrics.blockReadMb} />
            <Counter label={t("runtime.diskWrite")} mb={report.metrics.blockWriteMb} />
          </dl>

          {sampleIsStale(report.metrics.collectedAt) && (
            <p className="text-xs text-fg-subtle">
              Sampled {relativeTime(report.metrics.collectedAt)}.
            </p>
          )}
        </div>
      ) : report?.monitoringConfigured ? (
        <p className="border-t border-line pt-3 text-xs leading-5 text-fg-subtle">
          {t("runtime.noReadings")}
        </p>
      ) : (
        <p className="border-t border-line pt-3 text-xs leading-5 text-fg-subtle">
          {t("runtime.noMonitoring")}
        </p>
      )}
    </div>
  );
}

/**
 * How old a reading has to be before its age is worth saying out loud.
 *
 * The collector samples once a minute, so anything inside that window is simply
 * the current reading and stamping an age on it is noise — "Sampled 45 seconds
 * ago" under a live gauge reads as a caveat where there is nothing to caveat.
 *
 * Past two cycles it has missed one, and that is a different thing. The numbers
 * stay on screen because they were true when taken, but a collector that has
 * stopped must not look like one that is running: without this, a frozen
 * service shows plausible figures indefinitely with nothing to say otherwise.
 */
const STALE_AFTER_MS = 120_000;

function sampleIsStale(collectedAt: string | null): boolean {
  if (!collectedAt) return false;
  const age = Date.now() - Date.parse(collectedAt);
  return Number.isFinite(age) && age > STALE_AFTER_MS;
}

/**
 * A usage reading with its history drawn behind it.
 *
 * The value is the latest sample, shown as a gauge bar the way it always was;
 * the sparkline is the same series over the last hour, so a spike reads as a
 * spike rather than a number someone has to remember to compare.
 */
function UsageChart({
  label,
  value,
  detail,
  points,
}: {
  label: string;
  value: number | null;
  detail?: string | null;
  points: (number | null)[];
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <p className="micro">{label}</p>
        <p className="text-sm font-semibold tabular-nums">
          {value === null ? "—" : `${Math.round(value)}%`}
        </p>
      </div>
      <div className="mt-1.5 h-10 text-accent">
        <Sparkline points={points} />
      </div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-raised">
        {value !== null && (
          <div
            className={cx(
              "h-full rounded-full transition-[width]",
              value >= 90 ? "bg-danger" : value >= 75 ? "bg-warn" : "bg-mint",
            )}
            style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
          />
        )}
      </div>
      {detail && <p className="mt-1 text-xs text-fg-subtle">{detail}</p>}
    </div>
  );
}

/**
 * A tiny line chart, drawn only when there is something to draw.
 *
 * Percentages are plotted against a fixed 0–100 scale so the shape means the
 * same from one visit to the next — a line that sits at the top is always
 * "high", never "high relative to last week". Missing samples break the line
 * rather than being bridged, because a gap is not a value.
 */
function Sparkline({ points }: { points: (number | null)[] }) {
  const WIDTH = 100;
  const HEIGHT = 40;
  const readable = points.filter((p): p is number => p !== null);
  if (readable.length < 2) return null;

  const n = points.length;
  const segments: string[][] = [];
  let current: string[] = [];
  points.forEach((point, i) => {
    const x = (i / Math.max(1, n - 1)) * WIDTH;
    if (point === null) {
      if (current.length > 0) segments.push(current);
      current = [];
      return;
    }
    const y = HEIGHT - (Math.min(100, Math.max(0, point)) / 100) * HEIGHT;
    current.push(`${x.toFixed(2)},${y.toFixed(2)}`);
  });
  if (current.length > 0) segments.push(current);

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      preserveAspectRatio="none"
      className="h-full w-full"
      aria-hidden="true"
    >
      {segments.map((segment, i) => (
        <polyline
          key={i}
          points={segment.join(" ")}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </svg>
  );
}

/**
 * A cumulative figure since the container started, in megabytes.
 *
 * Missing renders as an em dash rather than as zero, for the same reason the
 * gauges do: "no reading" and "nothing transferred" are different claims, and
 * only one of them is safe to make up.
 */
function Counter({ label, mb }: { label: string; mb: number | null }) {
  return (
    <div>
      <dt className="micro">{label}</dt>
      <dd className="mt-1 text-sm text-fg tabular-nums">
        {mb === null ? <span className="text-fg-subtle">—</span> : formatMb(mb)}
      </dd>
    </div>
  );
}

/** MB up to a point, then GB — a container that has moved 4000 MB says 3.9 GB. */
function formatMb(mb: number): string {
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  if (mb >= 10) return `${Math.round(mb)} MB`;
  return `${mb.toFixed(1)} MB`;
}

function ratio(used: number | null, total: number | null): number | null {
  if (used === null || total === null || total <= 0) return null;
  return (used / total) * 100;
}

function pair(used: number | null, total: number | null): string | null {
  if (used === null || total === null) return null;
  return `${formatBytes(used)} of ${formatBytes(total)}`;
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="micro">
        {label}
      </dt>
      <dd className="mt-1 text-fg">{children}</dd>
    </div>
  );
}

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

